import { useJobDocumentActions } from "@/dentlab/src/hooks/actions/useJobDocumentActions";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { JobStatusEnum } from "@/lib/types/job";
import { JobDocumentTypeEnum } from "../job-document.types";
import { CustomButtonWithAnchoredMenu } from "@/dentlab/src/components/custom-button-with-anchored-menu/custom-button-with-anchored-menu.component";
import { CustomMenuItem } from "./job-document-customMenuItem.component";
import { PermissionStatus } from "@/dentlab/src/hooks/actions/actions-hooks-types";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import {
	PdfTemplateType,
	XmlTemplateType,
} from "@/dentlab/src/pdf-templates/document-template.types";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useNavigate } from "react-router-dom";
import {
	useTemplatesStore,
	TemplateItemInput,
} from "@/dentlab/src/store/Templates";
import { useState } from "react";
import { useCatalogsStore } from "@/dentlab/src/store/Catalogs";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { CreateNewTemplateDialog } from "../../../job-document-templates/job-document-template-dialoges/create-new-template.dialog";
import { useRecycleBinStore } from "@/dentlab/src/store/RecycleBin";
import { JobDocumentNoteDialog } from "@/dentlab/src/components/job-documents/job-document-note.dialog";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { calculateJobTotalsLegacy } from "@/dentlab/src/lib/utils/calculate";
import { GermanXmlExportDialog } from "./german-xml-export-dialog.component";
import { isRegion } from "@/dentlab/src/lib/utils/region-utils";
import { Region } from "@/dentlab/src/types/enums";
import { useStorage } from "@/dentlab/src/hooks/useStorage";
import { addCopyWatermark } from "@/dentlab/src/lib/utils/pdf-watermark";
import { dispatchPrint } from "@/dentlab/src/lib/utils/print-dispatch";

interface DocumentOption {
	text: string;
	action: () => void | Promise<void>;
	permission: PermissionStatus;
}

export const JobDocumentOptionsMenu: React.FC<{
	jobDocument: JobDocumentWithFiles;
	jobItems: JobItemEntityType[];
	onExport: (templateType: PdfTemplateType | XmlTemplateType) => void;
	onReExport?: (
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentWithFiles
	) => Promise<any>;
	onDocumentDuplicated?: (documentId: number) => void;
}> = ({
	jobDocument,
	jobItems,
	onExport,
	onReExport,
	onDocumentDuplicated,
}) => {
	const { createTemplate } = useTemplatesStore((state) => ({
		createTemplate: state.createTemplate,
	}));
	const { tariffsLookup } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup,
	}));
	const { articles } = useCentralStore((state) => ({
		articles: state.articles,
	}));
	const navigate = useNavigate();
	const {
		isArchiveJobDocumentAllowed,
		archiveJobDocument,
		isExportAuftragsblattAllowed,
		isExportRechnungAllowed,
		isExportBarzahlungAllowed,
		isExportKartenzahlungAllowed,
		isExportSwissXmlAllowed,
		isExportGermanXmlAllowed,
		isReExportLieferscheinAllowed,
		isReExportKostenvoranschlagAllowed,
	} = useJobDocumentActions();
	const [createTemplateDialogOpen, setCreateTemplateDialogOpen] =
		useState(false);
	const [jobDocumentNoteDialogOpen, setJobDocumentNoteDialogOpen] =
		useState(false);
	const [germanXmlExportDialogOpen, setGermanXmlExportDialogOpen] =
		useState(false);
	const {
		duplicateJobDocument,
		isCreateJobDocumentAllowed,
		isDeleteJobDocumentAllowed,
		job,
	} = useJobStore((state) => ({
		duplicateJobDocument: state.duplicateJobDocument,
		isCreateJobDocumentAllowed: state.isCreateJobDocumentAllowed,
		isDeleteJobDocumentAllowed: state.isDeleteJobDocumentAllowed,
		job: state.job,
	}));
	const timers = useJobStore((state) => state.timers);

	const { organization, client, activatedRegions } = useCentralStore(
		(state) => ({
			organization: state.organization,
			client: state.client,
			activatedRegions: state.activatedRegions,
		})
	);

	const { deleteJobDocumentTemporarily } = useRecycleBinStore((state) => ({
		deleteJobDocumentTemporarily: state.deleteJobDocumentTemporarily,
	}));

	const { fetchFileFromPath } = useStorage();

	const latestPdfFile = (jobDocument.files_with_shares || [])
		.filter(
			(f) =>
				!f.deleted_at &&
				f.path_name &&
				(f.file_name?.toLowerCase().endsWith(".pdf") ||
					(f.meta_data as { type?: string } | null)?.type === "pdf")
		)
		.sort((a, b) => {
			const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
			const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
			return bTime - aTime;
		})[0];

	const handlePrintAsCopy = async () => {
		if (!latestPdfFile?.path_name) {
			showNotification({
				message: "Keine PDF-Datei zum Drucken verfügbar",
				type: "error",
			});
			return;
		}
		const { success, data: signedUrl } = await fetchFileFromPath(
			latestPdfFile.path_name
		);
		if (!success || !signedUrl) {
			showNotification({
				message: "Datei konnte nicht geladen werden",
				type: "error",
			});
			return;
		}
		const res = await fetch(signedUrl);
		const originalBlob = await res.blob();
		const copyBlob = await addCopyWatermark(originalBlob);
		const fileName = `Kopie_${latestPdfFile.file_name ?? "Dokument.pdf"}`;
		await dispatchPrint({
			documentType: "copy",
			documentData: copyBlob,
			fileName,
		});
	};

	const regionId =
		client?.default_activated_region_id ??
		organization?.default_activated_region_id;

	const handleCreateTemplate = async (code: string, name: string) => {
		const templateItems: TemplateItemInput[] = jobItems.map((item) => {
			const input: TemplateItemInput = {
				quantity: item.quantity,
				code_e: item.code_e,
			};
			if (item.type === JobItemTypeEnum.TARIFF) {
				const tariffsByCode = tariffsLookup[item.code_e];
				if (tariffsByCode) {
					const tariffEntries = Object.values(tariffsByCode);
					const customTariff = tariffEntries.find((t) => t.is_custom);
					const defaultTariff = tariffEntries.find(
						(t) => !t.is_custom
					);
					if (customTariff) {
						input.tariffs_custom_id = customTariff.id;
					} else if (defaultTariff) {
						input.tariffs_default_id = defaultTariff.id;
					}
				}
			} else {
				const articlesByCode = articles[item.code_e];
				if (articlesByCode) {
					const firstArticle = Object.values(articlesByCode)[0];
					if (firstArticle) {
						input.article_id = firstArticle.id;
					}
				}
			}
			return input;
		});
		const result = await createTemplate(code, name, templateItems);
		if (result) {
			showNotification({
				message: "Jumbo/Vorlage erstellt",
				type: "success",
				action: {
					label: "Weiterleiten",
					onClick: () =>
						navigate(
							`/job-document-templates?templateId=${result.id}`
						),
				},
			});
		}
	};

	const handleDeleteJobDocument = async () => {
		if (await deleteJobDocumentTemporarily(jobDocument)) {
			showNotification({
				message: "Dokument erfolgreich gelöscht",
				action: {
					label: "Zum Papierkorb",
					onClick: () => {
						navigate(`/recycle-bin`);
					},
				},
				type: "success",
			});
		}
	};

	const documentOptions: DocumentOption[] = [
		{
			text: "Dokument als Vorlage speichern",
			action: async () => {
				if (Object.entries(timers).length > 0) {
					showNotification({
						message:
							"Bitte warten, bis die Elemente gespeichert wurden",
						type: "error",
					});
					return;
				}
				setCreateTemplateDialogOpen(true);
			},
			permission: {
				isAllowed: true,
				explanation: "",
			},
		},
	];

	if (jobDocument.status !== JobStatusEnum.ARCHIVED) {
		documentOptions.push({
			text: "Dokument ungültig machen",
			action: () => archiveJobDocument(jobDocument),
			permission: isArchiveJobDocumentAllowed(jobDocument),
		});
	}

	documentOptions.push({
		text: "Dokument in Papierkorb verschieben",
		action: handleDeleteJobDocument,
		permission: isDeleteJobDocumentAllowed(jobDocument.id),
	});

	documentOptions.push({
		text: "Dokument duplizieren",
		action: async () => {
			const newDocumentId = await duplicateJobDocument(
				jobDocument,
				jobItems ?? []
			);
			if (newDocumentId && onDocumentDuplicated) {
				onDocumentDuplicated(newDocumentId);
			}
		},
		permission: isCreateJobDocumentAllowed(
			jobDocument.type as JobDocumentTypeEnum
		),
	});

	if (jobDocument.type !== JobDocumentTypeEnum.DeliveryNote) {
		documentOptions.push({
			text: "Zu Lieferschein duplizieren",
			action: async () => {
				const newDocumentId = await duplicateJobDocument(
					jobDocument,
					jobItems ?? [],
					JobDocumentTypeEnum.DeliveryNote
				);
				if (newDocumentId && onDocumentDuplicated) {
					onDocumentDuplicated(newDocumentId);
				}
			},
			permission: isCreateJobDocumentAllowed(
				JobDocumentTypeEnum.DeliveryNote
			),
		});
	}

	if (
		jobDocument.type === JobDocumentTypeEnum.Quotation ||
		jobDocument.type === JobDocumentTypeEnum.DeliveryNote
	) {
		documentOptions.push({
			text: "Auftragsblatt exportieren",
			action: () => onExport(PdfTemplateType.AUFTRAGSBLATT),
			permission: isExportAuftragsblattAllowed(jobDocument),
		});
	}

	if (jobDocument.type === JobDocumentTypeEnum.DeliveryNote) {
		const invoiceAmount =
			job && client && organization
				? calculateJobTotalsLegacy({
						job,
						jobDocument,
						jobItems,
						organization,
						client,
					}).total.value
				: 0;

		documentOptions.push({
			text: "Rechnung erstellen",
			action: () => onExport(PdfTemplateType.RECHNUNG),
			permission: isExportRechnungAllowed(jobDocument),
		});

		documentOptions.push({
			text: "Barzahlungsquittung erstellen",
			action: () => onExport(PdfTemplateType.BARZAHLUNGSQUITTUNG),
			permission: isExportBarzahlungAllowed(jobDocument, invoiceAmount),
		});
		documentOptions.push({
			text: "Kartenzahlungsquittung erstellen",
			action: () => onExport(PdfTemplateType.KARTENZAHLUNGSQUITTUNG),
			permission: isExportKartenzahlungAllowed(
				jobDocument,
				invoiceAmount
			),
		});

		if (regionId && isRegion(regionId, Region.DE, activatedRegions)) {
			documentOptions.push({
				text: "VS XML erstellen",
				action: () => setGermanXmlExportDialogOpen(true),
				permission: isExportGermanXmlAllowed(jobItems, jobDocument),
			});
		}

		if (regionId && isRegion(regionId, Region.CH, activatedRegions)) {
			documentOptions.push({
				text: "SV LS ELNF erstellen",
				action: () => onExport(XmlTemplateType.LIEFERSCHEIN_XML),
				permission: isExportSwissXmlAllowed(jobItems, jobDocument),
			});
		}
		const reExportPermission = isReExportLieferscheinAllowed(
			jobDocument,
			jobItems,
			invoiceAmount
		);
		if (onReExport) {
			documentOptions.push({
				text: "Lieferschein erneut exportieren",
				action: async () => {
					await onReExport(jobItems, jobDocument);
				},
				permission: reExportPermission,
			});
		}
	}

	if (jobDocument.type === JobDocumentTypeEnum.Quotation) {
		const quotationAmount =
			job && client && organization
				? calculateJobTotalsLegacy({
						job,
						jobDocument,
						jobItems,
						organization,
						client,
					}).total.value
				: 0;

		if (regionId && isRegion(regionId, Region.CH, activatedRegions)) {
			documentOptions.push({
				text: "SV KV ELNF erstellen",
				action: () => onExport(XmlTemplateType.KOSTENVORANSCHLAG_XML),
				permission: isExportSwissXmlAllowed(jobItems, jobDocument),
			});
		}

		const reExportPermission = isReExportKostenvoranschlagAllowed(
			jobDocument,
			jobItems,
			quotationAmount
		);
		if (onReExport) {
			documentOptions.push({
				text: "Kostenvoranschlag erneut exportieren",
				action: async () => {
					await onReExport(jobItems, jobDocument);
				},
				permission: reExportPermission,
			});
		}
	}

	documentOptions.push({
		text: "Als Kopie drucken",
		action: handlePrintAsCopy,
		permission: {
			isAllowed: !!latestPdfFile,
			explanation: latestPdfFile
				? ""
				: "Keine PDF-Datei zum Drucken verfügbar",
		},
	});

	return (
		<CustomButtonWithAnchoredMenu
			data-testid="job-document-options-menu-trigger"
			aria-label="Dokumentoptionen öffnen"
		>
			{documentOptions.map((d) => {
				const { isAllowed, explanation } = d.permission;
				return (
					<div key={d.text}>
						<CustomMenuItem
							title={explanation ? explanation : ""}
							menuFunction={d.action}
							disabled={isAllowed === false}
							text={d.text}
							placement="left"
							dataTestId={`menu-item-${d.text
								.toLowerCase()
								.replace(/\s+/g, "-")}`}
						/>
					</div>
				);
			})}
			<CreateNewTemplateDialog
				onCreateTemplate={handleCreateTemplate}
				open={createTemplateDialogOpen}
				setOpen={setCreateTemplateDialogOpen}
			/>
			<JobDocumentNoteDialog
				open={jobDocumentNoteDialogOpen}
				onClose={() => setJobDocumentNoteDialogOpen(false)}
				jobDocument={jobDocument}
			/>
			<GermanXmlExportDialog
				open={germanXmlExportDialogOpen}
				onClose={() => setGermanXmlExportDialogOpen(false)}
				jobDocumentId={jobDocument.id}
			/>
		</CustomButtonWithAnchoredMenu>
	);
};
